'use client';

import { PortalModal, usePortalContext } from '@cloai/uikit';

import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { FC, useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { ImageGenerationType } from '@/.gql/graphql';
import { Header } from '@/src/components/Header';
import {
  useGTMContext,
  useProfileContext,
  useAutoMaskContext,
  useMobileContext,
  useImageGenerationContext,
} from '@/src/providers';
import { createFileFromImageSrc } from '@/src/utils/createFileFromImageSrc';
import { getMediaType } from '@/src/utils/getMediaType';

import {
  ActionButtons,
  downloadFileFromUrl,
  MediaPreview,
  PublishBlock,
} from './_components';
import { downloadFile } from '@tma.js/sdk-react';

const STYLES = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  wrapper: 'shrink-0 sm:max-w-[458px] m-auto bg-fill-menu p-5 pt-16',
  carouselContainer: 'relative overflow-hidden w-full h-[335px] mb-4',
  carousel: 'flex duration-700 cursor-pointer',
  scrollContainer: 'h-full overflow-y-auto',
} as const;

export const ProfilePhotoToolsPortal: FC = () => {
  const pathname = usePathname();
  const t = useTranslations('ProfilePage');
  const { activeImage, setActiveImageFile } = useImageGenerationContext();
  const { isMobiles } = useMobileContext();
  const sendGTM = useGTMContext();
  const profile = useProfileContext()!;
  const { closePortal, openPortal } = usePortalContext();
  const { setInpaintFile } = useAutoMaskContext();

  const mediaType = useMemo(
    () =>
      activeImage?.fileName ? getMediaType(activeImage.fileName) : 'unknown',
    [activeImage?.fileName],
  );

  const isAllowPublish = useMemo(() => {
    const allowTypes = [
      ImageGenerationType.GodMode,
      ImageGenerationType.ProModeTextToImagePro,
      ImageGenerationType.ProModeCreativePhoto,
    ];

    if ('type' in activeImage && allowTypes.includes(activeImage.type)) {
      return true;
    }

    return false;
  }, [activeImage]);

  const isVideo = useMemo(() => mediaType === 'video', [mediaType]);

  // Animation handler
  const handleAnimate = useCallback(async () => {
    if (!activeImage?.src) {
      return;
    }

    try {
      const file = await createFileFromImageSrc(activeImage.src, 'image.png');
      setActiveImageFile(file);
      closePortal();
      openPortal(PORTALS.AnimateProfilePhoto);
    } catch (error) {
      console.error('Failed to animate image:', error);
    }
  }, [activeImage?.src, setActiveImageFile, closePortal, openPortal]);

  // Edit handler
  const handleEdit = useCallback(async () => {
    if (!activeImage?.src) {
      return;
    }

    try {
      const file = await createFileFromImageSrc(activeImage.src, 'image.png');

      setInpaintFile((prev) => ({
        ...prev,
        id: 'inpaint-mode',
        fileUrl: activeImage.src,
        file,
      }));

      closePortal();
      openPortal(PORTALS.InpaintMode);
    } catch (error) {
      console.error('Failed to edit image:', error);
    }
  }, [activeImage?.src, setInpaintFile, closePortal, openPortal]);

  // Download handler
  const handleDownload = useCallback(async () => {
    if (!activeImage?.src) {
      return;
    }

    try {
      await downloadFileFromUrl(
        activeImage.src,
        activeImage.fileName,
        downloadFile,
      );
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }, [activeImage]);

  // Portal close handler
  const handleClosePortal = useCallback(() => {
    closePortal();
  }, [closePortal]);

  // Media load handler
  const handleMediaLoad = useCallback(() => {
    // Handle media load if needed
  }, []);

  return (
    <PortalModal
      portalId={PORTALS.ProfilePhotoTools}
      className={STYLES.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(STYLES.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>

        <div
          className={twMerge(STYLES.scrollContainer, isMobiles && 'pt-[100px]')}
        >
          <div className={STYLES.wrapper}>
            <div className={STYLES.carouselContainer}>
              <div className={STYLES.carousel}>
                <MediaPreview
                  activeFile={activeImage}
                  onMediaLoad={handleMediaLoad}
                  mediaType={mediaType}
                />
              </div>
            </div>

            {pathname.includes('profile') && (
              <PublishBlock
                publishedInFeedText={t('publishedInFeed')}
                publishInFeedText={t('publishInFeed')}
                publishedText={t('published')}
                nonPublishedText={t('nonPublished')}
                reusedText={t('reused')}
                publishInfoText={
                  isVideo ? t('publishVideoInfo') : t('publishInfo')
                }
                activeFile={activeImage}
                isAllowPublish={isAllowPublish}
                isAdmin={profile.isAdmin}
              />
            )}

            <ActionButtons
              isVideo={isVideo}
              onAnimate={handleAnimate}
              onDownload={handleDownload}
              onEdit={handleEdit}
              animateText={t('animate')}
              downloadText={t('download')}
              editText={t('edit')}
            />
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
